/**
 * Ticket fields — Step 28/29, rescoped by Step 94.
 *
 * WAS "the field library", listing every definition in the org. That reached
 * 106 rows, of which 64 existed solely because one catalogue item needed one
 * question. Kai: "all the service request ones should exist only under the
 * service request, not here" — and the reference tool agrees: its Field
 * Manager holds Ticket/Problem/Change/Release/Time-Entry fields and no
 * service item fields whatsoever.
 *
 * So this page now requests `?scope=ticket` and shows only fields on the
 * ticket form. A service request's own questions are edited from that
 * request; fields reused across several requests are `scope='shared'` and
 * appear in the item editor's picker, still not here.
 *
 * Define a field once (key, type,
 * base config); form layouts decide per-form whether it's required, whose
 * audience it's for, and any showWhen. `key` is always immutable; `type` is
 * reshapeable ONLY while the field has never been attached to a live layout
 * (`hasBeenUsed` false) — after that, changing type would make existing
 * answers unreadable under the new type's rules, so the server refuses it.
 */
import { Link } from 'react-router-dom';
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, LayoutList, ListPlus, Pencil, Plus, Trash2 } from 'lucide-react';
import { fieldsApi } from '../../features/fields/api';
import { FormLayoutEditor, type FormLayoutEditorHandle } from '../../features/fields/FormLayoutEditor';
import { apiFetch } from '../../lib/api';
import { toast } from 'sonner';
import { OptionsEditor, TYPE_LABELS } from '../../features/fields/FieldConfigControls';
import { qk } from '../../lib/queryClient';
import { useDirty } from '../../lib/dirty';
import { useUrlState } from '../../lib/urlState';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Modal } from '../../components/ui/modal';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import type { FieldDefinition, FieldType, SelectOption } from '@itsm/shared';
import { FIELD_TYPES } from '@itsm/shared';
import { useConfirm } from '../../lib/confirm';

const HAS_OPTIONS: FieldType[] = ['select', 'multiselect', 'radio'];

export function FieldLibraryPage() {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.fieldDefinitions,
    queryFn: () => fieldsApi.list('ticket'),
  });
  // Step 149 — hoisted above the early returns (rules-of-hooks).
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [editing, setEditing] = useState<FieldDefinition | 'new' | null>(null);
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [helpText, setHelpText] = useState('');
  const [type, setType] = useState<FieldType>('text');
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [allowOther, setAllowOther] = useState(false);
  // Whether archived fields are shown is a VIEW — someone sending 'look at the
  // archived ones' needs the link to arrive showing them.
  const [showArchivedRaw, setShowArchived] = useUrlState('archived', '');
  const showArchived = showArchivedRaw === '1';
  // The lifecycle dialog (Step 67): opened for one field, it fetches the
  // usage inspector FIRST and shapes itself around the answer — Delete is
  // only ever offered for a field nothing references.
  const [lifecycle, setLifecycle] = useState<FieldDefinition | null>(null);
  const [lifecycleError, setLifecycleError] = useState<string | null>(null);
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: qk.fieldDefinitions });
  const typeLocked = editing !== 'new' && editing !== null && !!editing.hasBeenUsed;

  function openNew() {
    setEditing('new');
    setKey('');
    setLabel('');
    setHelpText('');
    setType('text');
    setOptions([]);
    setAllowOther(false);
  }
  function openEdit(f: FieldDefinition) {
    setEditing(f);
    setKey(f.key);
    setLabel(f.label);
    setHelpText(f.helpText ?? '');
    setType(f.type);
    setOptions(f.config.options ?? []);
    setAllowOther(!!f.config.allowOther);
  }

  const save = useMutation({
    mutationFn: () => {
      const config = HAS_OPTIONS.includes(type)
        ? { options: options.filter((o) => o.label.trim()), allowOther }
        : {};
      return editing === 'new'
        ? fieldsApi.create({ key: key.trim(), label: label.trim(), helpText: helpText.trim(), type, config })
        : fieldsApi.update(editing!.id, {
            label: label.trim(),
            helpText: helpText.trim(),
            ...(typeLocked ? {} : { type }),
            config,
          });
    },
    onSuccess: (res) => {
      if (res.ok) {
        setEditing(null);
        invalidate();
      }
    },
  });

  const usage = useQuery({
    queryKey: qk.fieldUsage(lifecycle?.id ?? ''),
    queryFn: () => fieldsApi.usage(lifecycle!.id),
    enabled: !!lifecycle,
  });
  const setArchived = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      fieldsApi.update(id, { isActive: !archived }),
    onSuccess: (res) => {
      if (res.ok) {
        setLifecycle(null);
        invalidate();
      } else setLifecycleError(res.error ?? 'Could not update the field');
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => fieldsApi.remove(id),
    onSuccess: (res) => {
      if (res.ok) {
        setLifecycle(null);
        invalidate();
      } else setLifecycleError(res.error ?? 'Could not delete the field');
    },
  });

  /**
   * Dirty against the definition being edited. Config is compared through its
   * serialised form because it is a nested object.
   *
   * ABOVE the loading and error returns, and that placement is the bug fix,
   * not a tidy-up: this sat below them, so the first render (query pending)
   * ran fewer hooks than the second (data arrived) and React threw #310 —
   * "rendered more hooks than during the previous render". It depends only on
   * component state, never on `data`, so there was never a reason for it to
   * be down there. Same lesson as BusinessHoursPage; second time now.
   */
  const dirty =
    editing === 'new'
      ? !!label.trim() || !!key.trim()
      : !!editing &&
        (label !== editing.label ||
          key !== editing.key ||
          type !== editing.type ||
          (helpText ?? '') !== (editing.helpText ?? ''));
  useDirty(dirty && editing !== null);

  if (isPending)
    return (
      <div className="max-w-3xl space-y-3 p-6">
        <Skeleton className="h-6 w-44" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  if (isError)
    return (
      <div className="m-6 max-w-3xl space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
        <p className="text-sm font-medium text-destructive">Could not load the field library</p>
        <p className="text-xs text-destructive/80">{(error as Error).message}</p>
        <button
          onClick={() => refetch()}
          className="text-xs text-destructive/80 underline hover:text-destructive"
        >
          Retry
        </button>
      </div>
    );

  const archivedCount = data!.entries.filter((f) => !f.isActive).length;
  const shown = data!.entries.filter((f) => showArchived || f.isActive);

  return (
    <div className="max-w-3xl p-6">
      <div className="mb-1 flex items-center gap-3">
        <ListPlus className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Ticket fields</h1>
        <div className="flex-1" />
        <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked ? '1' : '')}
            className="h-3.5 w-3.5 rounded border-input"
          />
          Show archived ({archivedCount})
        </label>
        {/* Step 149 — the rail IS a layout now. Rows, order, labels and which
            custom fields appear on every ticket are edited here, in the same
            builder catalogue forms use. */}
        <Button size="sm" variant="outline" onClick={() => setLayoutOpen(true)}>
          <LayoutList /> Properties layout
        </Button>
        <Button size="sm" onClick={openNew}>
          <Plus /> New field
        </Button>
      </div>
      {layoutOpen && <PropertiesLayoutModal onClose={() => setLayoutOpen(false)} />}
      <p className="mb-4 text-sm text-muted-foreground">
        Define once, reuse across every form layout. Key is always locked; type stays editable until the field
        is actually in use on a live form.
      </p>

      <div className="divide-y rounded-lg border bg-card">
        {shown.map((f) => {
          const archived = !f.isActive;
          return (
            <div key={f.id} className="flex items-center gap-2 px-3 py-2.5 text-sm">
              <Badge variant="outline">{TYPE_LABELS[f.type]}</Badge>
              <span className={cn('min-w-0 flex-1 truncate', archived && 'text-muted-foreground')}>
                {f.label}
              </span>
              {archived && (
                <Badge className="shrink-0 border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400">
                  Archived
                </Badge>
              )}
              <span className="shrink-0 font-mono text-xs text-muted-foreground">{f.key}</span>
              <Button variant="ghost" size="icon" aria-label={`Edit ${f.label}`} onClick={() => openEdit(f)}>
                <Pencil className="h-4 w-4 text-muted-foreground" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Archive or delete ${f.label}`}
                onClick={() => {
                  setLifecycleError(null);
                  setLifecycle(f);
                }}
              >
                {archived ? (
                  <ArchiveRestore className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Archive className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
          );
        })}
        {shown.length === 0 && (
          <div className="p-8 text-center">
            <p className="text-sm text-muted-foreground">
              {data!.entries.length === 0
                ? 'No custom ticket fields yet.'
                : 'No active fields — tick "Show archived" to see the rest.'}
            </p>
            {/* Step 94. This page used to list all 106 definitions, including
                64 that existed only because one catalogue item needed one
                question. A service request's fields belong to that request,
                not here — so the empty state EXPLAINS where they went rather
                than looking broken. */}
            {data!.entries.length === 0 && (
              <p className="mx-auto mt-2 max-w-md text-xs leading-relaxed text-muted-foreground">
                Fields you add to a service request live on that request, and are edited from it — they are
                not listed here. This page is for fields on the ticket form itself.{' '}
                <Link to="/admin/catalogue" className="underline hover:text-foreground">
                  Open the service catalogue
                </Link>{' '}
                to edit a request's own fields.
              </p>
            )}
          </div>
        )}
      </div>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'New field' : 'Edit field'}
      >
        <div className="space-y-3 p-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Key (immutable)</label>
            <Input
              value={key}
              onChange={(e) => setKey(e.target.value)}
              disabled={editing !== 'new'}
              placeholder="staff_member"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Label</label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Staff member" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Help text</label>
            <Input value={helpText} onChange={(e) => setHelpText(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Type {typeLocked && '(in use on a live form — locked)'}
            </label>
            <Select value={type} onChange={(e) => setType(e.target.value as FieldType)} disabled={typeLocked}>
              {FIELD_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t]}
                </option>
              ))}
            </Select>
          </div>
          {HAS_OPTIONS.includes(type) && (
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Choices</label>
              <OptionsEditor options={options} onChange={setOptions} />
              <label className="mt-1.5 flex items-center gap-1.5 text-xs">
                <input
                  type="checkbox"
                  checked={allowOther}
                  onChange={(e) => setAllowOther(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-input"
                />
                Allow "Other" (free text)
              </label>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              disabled={!dirty || !label.trim() || !key.trim() || save.isPending}
              onClick={() => save.mutate()}
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>

      {/* Step 67's lifecycle dialog: the usage inspector decides what this
          dialog is allowed to offer. Archive is always safe; Delete only
          appears for a field nothing has ever referenced. */}
      <Modal
        open={lifecycle !== null}
        onClose={() => setLifecycle(null)}
        title={lifecycle && !lifecycle.isActive ? 'Restore field' : 'Archive or delete field'}
      >
        <div className="space-y-3 p-4">
          <p className="text-sm">
            <span className="font-medium">{lifecycle?.label}</span>{' '}
            <span className="font-mono text-xs text-muted-foreground">{lifecycle?.key}</span>
          </p>

          {usage.isPending && <Skeleton className="h-24 w-full" />}
          {usage.isError && (
            <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
              <p className="text-sm font-medium text-destructive">Could not check where this field is used</p>
              <button
                onClick={() => usage.refetch()}
                className="text-xs text-destructive/80 underline hover:text-destructive"
              >
                Retry
              </button>
            </div>
          )}

          {usage.data && (
            <div className="rounded-md border bg-muted/20 p-3 text-xs">
              <p className="mb-1.5 font-medium">Where this field is used</p>
              {usage.data.layouts.length === 0 && usage.data.valueCount === 0 ? (
                <p className="text-muted-foreground">
                  Nowhere yet — no form uses it and no ticket holds an answer.
                </p>
              ) : (
                <ul className="space-y-1 text-muted-foreground">
                  {usage.data.layouts.map((l) => (
                    <li key={l.id}>
                      <span className="text-foreground">{l.name}</span>
                      {l.items.length > 0 && <> — {l.items.map((it) => it.name).join(', ')}</>}
                    </li>
                  ))}
                  {usage.data.valueCount > 0 && (
                    <li>
                      <span className="text-foreground">{usage.data.valueCount}</span> ticket
                      {usage.data.valueCount === 1 ? '' : 's'} already hold an answer to it.
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}

          {lifecycle && !lifecycle.isActive ? (
            <p className="text-sm text-muted-foreground">
              Restoring puts this field back in the builder and its pickers. Tickets holding an answer were
              never affected.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Archiving removes this field from everywhere except history and reporting — tickets that already
              hold an answer keep rendering it, and you can restore it at any time.
            </p>
          )}

          {lifecycleError && <p className="text-sm text-destructive">{lifecycleError}</p>}

          <div className="flex flex-wrap justify-end gap-2 border-t pt-3">
            <Button variant="outline" onClick={() => setLifecycle(null)}>
              Cancel
            </Button>
            {usage.data?.canDelete && (
              <Button
                variant="outline"
                className="border-destructive/40 text-destructive hover:bg-destructive/10"
                disabled={remove.isPending}
                onClick={async () => {
                  if (
                    await confirm({
                      title: `Permanently delete “${lifecycle!.label}”?`,
                      body: 'This cannot be undone. Nothing references it, so nothing else changes.',
                      destructive: true,
                      confirmLabel: 'Delete permanently',
                    })
                  ) {
                    setLifecycleError(null);
                    remove.mutate(lifecycle!.id);
                  }
                }}
              >
                <Trash2 /> Delete permanently
              </Button>
            )}
            <Button
              disabled={setArchived.isPending}
              onClick={() => {
                setLifecycleError(null);
                setArchived.mutate({ id: lifecycle!.id, archived: !!lifecycle!.isActive });
              }}
            >
              {lifecycle && !lifecycle.isActive ? (
                <>
                  <ArchiveRestore /> Restore
                </>
              ) : (
                <>
                  <Archive /> Archive
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/**
 * Step 149 — the "Ticket properties" layout, in the same builder catalogue
 * forms use.
 *
 * The layout id is looked up rather than passed: there is exactly one per org
 * at `ticket` scope and the seed created it, so the page never has to know
 * it. Save goes through the editor's own handle — the builder owns its draft
 * and its dirty state, and this modal only decides when to ask it to write.
 */
function PropertiesLayoutModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const ref = useRef<FormLayoutEditorHandle>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  useDirty(dirty);
  const layout = useQuery({
    queryKey: qk.propertiesLayoutAdmin,
    queryFn: () =>
      apiFetch<{ entries: { id: string; name: string }[] }>('/api/admin/form-layouts?scope=ticket'),
  });
  const layoutId = layout.data?.entries[0]?.id ?? null;
  return (
    <Modal open onClose={onClose} title="Ticket properties" wide>
      <p className="mb-3 text-sm text-muted-foreground">
        The rows on every ticket's properties panel, in this order. Rename a label, hide a row by removing it,
        or add one of your own fields so it appears on every ticket. Built-in properties keep their key and
        type.
      </p>
      {layout.isPending && <Skeleton className="h-64 w-full" />}
      {layout.isError && <p className="text-sm text-destructive">The layout could not be loaded.</p>}
      {layoutId && <FormLayoutEditor ref={ref} layoutId={layoutId} onDirtyChange={setDirty} />}
      <div className="mt-4 flex items-center justify-end gap-2 border-t pt-3">
        {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
        <Button
          disabled={!dirty || saving || !layoutId}
          onClick={async () => {
            setSaving(true);
            const id = await ref.current?.save('Ticket properties');
            setSaving(false);
            if (!id) return toast.error('The layout could not be saved.');
            void qc.invalidateQueries({ queryKey: qk.tickets.propertiesLayout });
            void qc.invalidateQueries({ queryKey: qk.propertiesLayoutAdmin });
            setDirty(false);
            toast.success('Saved. Every ticket now renders from this layout.');
          }}
        >
          {saving ? 'Saving…' : 'Save layout'}
        </Button>
      </div>
    </Modal>
  );
}
